import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Bell, ChevronDown, LogOut, Moon, Settings, Sun, User, Users } from 'lucide-react';
import { SUPPORTED_LOCALES, setLocale } from '../../lib/i18n/i18n';
import { FlagIcon } from '../../components/ui/FlagIcon';
import { useAuth } from '../auth/AuthContext';
import { useModules } from '../modules/ModuleContext';
import { TourButton } from '../tour/TourButton';
import { useTheme } from '../theme/ThemeContext';
import { EntitySwitcher } from '../../components/EntitySwitcher';
import { ProcessingQueueBadge } from '../../modules/zillodocs/components/ProcessingQueueBadge';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface HeaderProps {
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('layout');
  const { user, logout, isSiteAdmin, isOwner } = useAuth();
  const { currentModule } = useModules();
  const { theme, toggleTheme } = useTheme();
  const [notifications, setNotifications] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBadges = () => {
    api.getEquipeBadges().then(r => {
      if (r.success && r.data) {
        const d = r.data;
        setNotifications(d.alertes_actives + d.conges_attente + d.primes_brouillon + d.entretiens_planifies + (d.signalements_ouverts || 0));
      }
    }).catch(() => {/* silently ignore */});
  };

  // Badges only for admin/owner (equipe.alertes permission required)
  const canSeeBadges = !isSiteAdmin && (isOwner || user?.role === 'admin' || user?.role === 'dentist');

  useEffect(() => {
    if (!canSeeBadges) return;
    fetchBadges();
    intervalRef.current = setInterval(fetchBadges, 30000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [canSeeBadges]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="h-[60px] bg-white dark:bg-gray-900 border-b border-neutral-200 dark:border-gray-700 z-50 flex-shrink-0">
      <div className="flex items-center justify-between h-full px-4">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img src="/logo.png" alt="Dental Manager" className="h-9" />
          </button>

          {/* Entity Switcher (multi-account) */}
          <EntitySwitcher />
        </div>

        {/* Center — Date */}
        <p className="text-sm font-bold text-gray-700 dark:text-gray-200 capitalize hidden sm:block">
          {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
        </p>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* ZilloDocs Processing Queue */}
          {currentModule?.id === 'zillodocs' && <ProcessingQueueBadge />}

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-gray-800 transition-colors"
            title={t(theme === 'dark' ? 'header.switchToLight' : 'header.switchToDark')}
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Notifications */}
          <button className="relative p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-gray-800 transition-colors">
            <Bell size={20} />
            {notifications > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {notifications}
              </span>
            )}
          </button>

          {/* Tour Button */}
          <TourButton />

          {/* User Menu */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-gray-800 dark:text-gray-200 transition-colors">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-medium text-sm">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
              )}
              <span className="hidden sm:inline text-sm font-medium dark:text-slate-200">
                {user?.firstName} {user?.lastName}
              </span>
              <ChevronDown size={14} className="dark:text-slate-400" />
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="min-w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-neutral-200 dark:border-gray-700 py-1 z-50"
                sideOffset={5}
                align="end"
              >
                <div className="px-3 py-2 border-b border-neutral-200 dark:border-gray-700">
                  <p className="text-sm font-medium dark:text-gray-100">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-gray-400">{user?.email}</p>
                </div>

                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-neutral-100 dark:hover:bg-gray-700 dark:text-gray-200 outline-none"
                  onClick={() => navigate('/profile')}
                >
                  <User size={16} />
                  <span>{t('userMenu.profile')}</span>
                </DropdownMenu.Item>

                {!isSiteAdmin && (
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-neutral-100 dark:hover:bg-gray-700 dark:text-gray-200 outline-none"
                    onClick={() => navigate('/membres')}
                  >
                    <Users size={16} />
                    <span>{t('userMenu.users')}</span>
                  </DropdownMenu.Item>
                )}

                {!isSiteAdmin && (
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-neutral-100 dark:hover:bg-gray-700 dark:text-gray-200 outline-none"
                    onClick={() => navigate('/settings')}
                  >
                    <Settings size={16} />
                    <span>{t('userMenu.settings')}</span>
                  </DropdownMenu.Item>
                )}

                {/* Language picker — inline flag row */}
                <div className="flex items-center justify-center gap-1 px-3 py-2">
                  {SUPPORTED_LOCALES.map(({ code }) => (
                    <button
                      key={code}
                      onClick={() => { setLocale(code); api.setUserUiSetting('locale', code).catch(() => {}); }}
                      className={`p-1.5 rounded-md transition-colors ${
                        i18n.language === code
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 ring-1 ring-emerald-400 dark:ring-emerald-600'
                          : 'hover:bg-neutral-100 dark:hover:bg-gray-700'
                      }`}
                      title={SUPPORTED_LOCALES.find(l => l.code === code)?.label}
                    >
                      <FlagIcon code={code} size={20} />
                    </button>
                  ))}
                </div>

                <DropdownMenu.Separator className="h-px bg-neutral-200 dark:bg-gray-700 my-1" />

                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 outline-none"
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                  <span>{t('userMenu.logout')}</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </header>
  );
}

export default Header;
